import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import MuiStack from '@mui/material/Stack'
import MuiIconButton from '@mui/material/IconButton'
import MuiLinearProgress from '@mui/material/LinearProgress'
import DeleteIcon from '@mui/icons-material/Delete'
import { DataGridPro as MuiDataGrid, GridToolbar as MuiGridToolBar } from '@mui/x-data-grid-pro'
import { LicenseInfo as MuiLicenseInfo } from '@mui/x-license-pro'

import DeleteUser from './deleteUser'
import ConfirmDialog from './confirmDialog'

import { useGetUsersQuery, usePutUserMutation, useEnableUserMutation, useDisableUserMutation } from '../slice'
import { useGetFieldsQuery } from '../../../services/fields/slice'
import { getUpdatedFields } from './utils'
import AppConfig from '../../../config'

MuiLicenseInfo.setLicenseKey(AppConfig.mui.licenseKey)

const UsersList = () => {
  const [rows, setRows] = useState([])
  const [showDeleteUser, setShowDeleteUser] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [promiseArguments, setPromiseArguments] = useState(null)
  const [saveArguments, setSaveArguments] = useState(null)

  const { user: loggedInUser } = useSelector((state) => state.persisted.auth)
  const { data: users, isUsersLoading } = useGetUsersQuery()
  const { data: userFields, isLoading: isFieldsLoading } = useGetFieldsQuery('users')
  const [enableUser, { isLoading: isEnableUserLoading }] = useEnableUserMutation()
  const [disableUser, { isLoading: isDisableUserLoading }] = useDisableUserMutation()
  const [putUser, { isLoading: isPutUserLoading }] = usePutUserMutation()

  const onClickDeleteUser = useCallback((user) => {
    setCurrentUser(user)
    setShowDeleteUser(true)
  }, [])

  useEffect(() => {
    if (!users) return
    setRows(users)
  }, [users])

  const columns = useMemo(() => {
    if (userFields && users) {
      const userColumns = userFields
        .map((userField) => {
          const type = userField.type === 'boolean' ? 'boolean' : userField.type === 'date' ? 'dateTime' : undefined
          return {
            field: userField.name,
            headerName: userField.label,
            description: userField.description,
            width: userField.width || 200,
            editable: !userField.isReadOnly,
            type,
            priority: userField.priority || 1000,
            valueGetter: (params) => {
              if (params.field === 'userLastModifiedDate') return new Date(params.value)
              return params.value
            },
          }
        })
        .sort((a, b) => a.priority - b.priority)

      return [
        ...userColumns,
        {
          field: 'actions',
          headerName: 'Action',
          flex: 1,
          sortable: false,

          renderCell: (params) => {
            if (params?.row?.sub === loggedInUser?.sub) {
              return null
            }
            const onDeleteClick = () => onClickDeleteUser(params.row)

            return (
              <MuiStack direction="row">
                <MuiIconButton size="small" onClick={onDeleteClick}>
                  <DeleteIcon />
                </MuiIconButton>
              </MuiStack>
            )
          },
        },
      ]
    }
    return []
  }, [userFields, users, loggedInUser, onClickDeleteUser])

  const processRowUpdate = (newRow, oldRow) => {
    return new Promise((resolve, reject) => {
      const mutation = getUpdatedFields(newRow, oldRow)
      if (mutation.length === 1 && mutation[0] === 'enabled') {
        setPromiseArguments({ resolve, reject, newRow, oldRow })
      } else if (mutation.length === 1) {
        setSaveArguments({ resolve, reject, newRow, oldRow })
      } else {
        resolve(oldRow) // Nothing was changed
      }
    })
  }

  const handleSave = useCallback(async () => {
    const { newRow, oldRow, reject, resolve } = saveArguments

    const userUpdate = {
      sub: newRow.sub,
      email: newRow.email,
      familyName: newRow.familyName,
      givenName: newRow.givenName,
      phoneNumber: newRow.phoneNumber,
      isAdmin: newRow.isAdmin,
    }
    try {
      const result = await putUser(userUpdate)
      if (result?.error) {
        reject(oldRow)
      } else {
        resolve(newRow)
      }
    } catch (error) {
      reject(oldRow)
    } finally {
      setSaveArguments(null)
    }
  }, [saveArguments, putUser])

  useEffect(() => {
    if (saveArguments) {
      handleSave()
    }
  }, [saveArguments, handleSave])

  const handleNo = useCallback(() => {
    const { oldRow, resolve } = promiseArguments
    resolve(oldRow)
    setPromiseArguments(null)
  }, [promiseArguments])

  const handleYes = useCallback(async () => {
    const { newRow, oldRow, reject, resolve } = promiseArguments

    try {
      await (newRow.enabled ? enableUser(newRow) : disableUser(newRow))
      resolve(newRow)
      setPromiseArguments(null)
    } catch (error) {
      reject(oldRow)
      setPromiseArguments(null)
    }
  }, [promiseArguments, enableUser, disableUser])

  const handleProcessRowUpdateError = useCallback((error) => {
    return null
  }, [])

  return (
    <>
      <ConfirmDialog
        promiseArguments={promiseArguments}
        handleNo={handleNo}
        handleYes={handleYes}
        loading={isEnableUserLoading || isDisableUserLoading}
      />
      <MuiDataGrid
        rows={rows}
        columns={columns}
        density="compact"
        loading={isUsersLoading || isFieldsLoading || isPutUserLoading}
        pagination
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        slots={{
          loadingOverlay: MuiLinearProgress,
          toolbar: MuiGridToolBar,
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        sx={{
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
            outline: 'none !important',
          },
        }}
      />
      <DeleteUser user={currentUser} open={showDeleteUser} setOpen={setShowDeleteUser} />
    </>
  )
}

export default UsersList
